
import React, { useEffect } from 'react';
import { Calendar, Clock, MapPin, Phone, Mail } from 'lucide-react';

interface SetmoreBookingProps {
  setmoreUrl?: string;
}

const SetmoreBooking: React.FC<SetmoreBookingProps> = ({
  setmoreUrl = "https://nearlabservicesllc.setmore.com"
}) => {
  useEffect(() => {
    console.log('SetmoreBooking component mounted, loading script...');
    // Load Setmore widget script
    const script = document.createElement('script');
    script.src = 'https://assets.setmore.com/integration/static/setmoreIframeLive.js';
    script.id = 'setmore_script';
    script.type = 'text/javascript';
    
    script.onload = () => {
      console.log('Setmore script loaded successfully');
    };
    
    script.onerror = () => {
      console.error('Failed to load Setmore script');
    };
    
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      console.log('SetmoreBooking component unmounting, cleaning up script...');
      const existingScript = document.getElementById('setmore_script');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <section className="py-16 px-4 bg-gradient-to-br from-primary-light via-blue-light/50 to-green-light/50 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-primary/10 via-transparent to-green-primary/10"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Calendar className="text-blue-primary animate-pulse" size={28} />
            <Clock className="text-green-primary animate-pulse" size={28} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-primary to-green-primary bg-clip-text text-transparent mb-4">
            Schedule Your Appointment
          </h1>
          <p className="text-gray-700 max-w-2xl mx-auto text-lg">
            Book your DNA testing, drug screening, or other lab services with our convenient online scheduling system.
          </p>
          <div className="w-24 h-1 bg-gradient-blue-green mx-auto mt-4 rounded-full"></div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center p-6 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/50">
            <Calendar className="w-10 h-10 text-blue-primary mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">Choose Your Date</h3>
            <p className="text-gray-600">
              Select from available appointment slots that fit your schedule
            </p>
          </div>
          
          <div className="text-center p-6 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/50">
            <Clock className="w-10 h-10 text-green-primary mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">Pick Your Time</h3>
            <p className="text-gray-600">
              Morning, afternoon, or evening - we work around your availability
            </p>
          </div>
          
          <div className="text-center p-6 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-white/50">
            <MapPin className="w-10 h-10 text-blue-primary mx-auto mb-4" />
            <h3 className="font-semibold text-gray-900 mb-3 text-lg">Your Location</h3>
            <p className="text-gray-600">
              We come to your home, office, or any convenient location
            </p>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-xl border border-white/50 p-8 mb-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Schedule?</h2>
            <p className="text-gray-700 mb-6">
              Use our online booking system below or contact us directly for immediate assistance.
            </p>
            
            {/* Embedded Booking Iframe */}
            <div className="w-full h-[600px] border border-gray-200 rounded-lg overflow-hidden mb-6">
              <iframe 
                src={setmoreUrl} 
                scrolling="yes" 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                allow="web-share; payment"
                title="Online Booking System"
                className="w-full h-full"
              />
            </div>
            
            <div className="flex items-center justify-center gap-4 text-gray-600">
              <span>Need help?</span>
              <a href="tel:7135104442" className="flex items-center gap-2 hover:text-blue-primary transition-colors">
                <Phone className="w-4 h-4" />
                713 510 4442
              </a>
              <span>or</span>
              <a href="tel:7135613518" className="flex items-center gap-2 hover:text-blue-primary transition-colors">
                <Phone className="w-4 h-4" />
                713 561 3518
              </a>
            </div>
          </div>
        </div>

        <div className="bg-blue-50/80 backdrop-blur-sm rounded-xl p-6 border border-blue-200/50">
          <h3 className="font-semibold text-blue-900 mb-4 text-center">Need Help?</h3>
          <div className="grid md:grid-cols-2 gap-4 text-center">
            <div className="flex items-center justify-center gap-2 text-blue-700">
              <Phone className="w-4 h-4" />
              <span>713 510 4442 · 713 561 3518</span>
            </div>
            <div className="flex items-center justify-center gap-2 text-blue-700">
              <Mail className="w-4 h-4" />
              <span>Email: info@nearlabservices.com</span>
            </div>
          </div>
        </div>

        {/* Floating Setmore Button */}
        <a 
          style={{
            position: 'fixed',
            right: '-2px',
            top: '25%',
            display: 'block',
            zIndex: 20000
          }} 
          target="_blank"
          href={setmoreUrl}
          onClick={() => console.log('Floating Setmore button clicked')}
        >
          <img 
            style={{
              border: 'none'
            }} 
            src="https://storage.googleapis.com/full-assets/setmore/images/1.0/Calendar/Setmore-Book-Now.png" 
            alt="Book an appointment with NearLab Services Llc using Setmore"
            onLoad={() => console.log('Setmore button image loaded successfully')}
            onError={() => console.error('Failed to load Setmore button image')}
          />
        </a>
      </div>
    </section>
  );
};

export default SetmoreBooking;
